import { useState } from "react";
import api, { rupees } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { LoadingBlock } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Heart, ArrowRight, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Simulation() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/ai/simulation");
            setResult(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI service failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="simulation-page">
            <PageHeader
                eyebrow="What if I'm gone today"
                title="A private rehearsal — for the day you hope never comes."
                subtitle="A warm, narrative simulation of how your family would receive help, what they'd claim, and where the gaps are."
                testid="sim-header"
                actions={
                    <Button onClick={run} disabled={busy} data-testid="sim-run" className="rounded-full">
                        <Heart className="mr-2 h-4 w-4" /> {busy ? "Running…" : result ? "Re-run" : "Run simulation"}
                    </Button>
                }
            />
            <div className="px-8 py-10">
                {busy ? <LoadingBlock label="Simulating handover" /> : !result ? (
                    <div className="rounded-lg border border-dashed border-border p-10 text-center">
                        <Heart className="mx-auto h-8 w-8 text-accent" strokeWidth={1.5} />
                        <h3 className="mt-3 font-display text-lg font-semibold">Why this matters</h3>
                        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                            Most "what-if" anxiety dissolves when you can see the actual sequence. This screen gives
                            your spouse, child, or executor a living script they could follow. The richer your vault,
                            the kinder the rehearsal.
                        </p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 gap-8 max-w-6xl">
                        <article className="lg:col-span-2 rounded-2xl border border-primary bg-primary text-primary-foreground p-8">
                            <div className="text-xs uppercase tracking-[0.2em] font-semibold opacity-80">Letter to your family</div>
                            <p data-testid="sim-narrative" className="mt-4 font-display text-xl leading-snug">
                                {result.narrative}
                            </p>
                        </article>
                        <aside className="rounded-2xl border border-border bg-card p-6">
                            <div className="label-tag">Estimated life-cover payout</div>
                            <div data-testid="sim-payout" className="mt-2 font-display text-3xl font-extrabold font-mono">
                                {rupees(result.estimated_payout)}
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">From the policies in your vault. Verify with each insurer.</p>
                        </aside>

                        <section className="lg:col-span-2 rounded-lg border border-border bg-card p-6" data-testid="sim-sequence">
                            <h3 className="font-display text-lg font-semibold mb-4">Step-by-step handover</h3>
                            <ol className="space-y-3">
                                {(result.sequence || []).map((s, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm">
                                        <span className="font-mono text-primary text-base font-bold w-6 shrink-0">{(i + 1).toString().padStart(2, "0")}</span>
                                        <span className="leading-relaxed">{s}</span>
                                    </li>
                                ))}
                            </ol>
                        </section>

                        <section className="rounded-lg border border-accent/40 bg-accent/5 p-6" data-testid="sim-weak">
                            <div className="flex items-center gap-2 mb-3">
                                <AlertTriangle className="h-4 w-4 text-accent" />
                                <h3 className="font-display text-lg font-semibold">Weak links</h3>
                            </div>
                            <ul className="space-y-2 text-sm">
                                {(result.weak_links || []).map((w, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent" />
                                        {w}
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <p className="lg:col-span-3 text-xs text-muted-foreground inline-flex items-center gap-2">
                            <ArrowRight className="h-3 w-3" />
                            Plug each weak link by adding the missing nominee, document, or contact in the relevant module.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
